"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Apariencia de la app.
// Apple HIG (modo oscuro) pide no ofrecer un ajuste propio de la app, porque la persona espera que la
// app siga el ajuste del sistema. Por pedido del equipo hay un selector, pero respeta esa idea:
// - "Sistema" es el valor por defecto y sigue al sistema, incluso si cambia con la app abierta;
// - "Claro" y "Oscuro" son excepciones explícitas que se recuerdan en este navegador.
export type Theme = "light" | "dark";
export type ThemePreference = "system" | Theme;

const QUERY = "(prefers-color-scheme: dark)";
const STORAGE_KEY = "seaelec-appearance";

type ThemeContextValue = {
  theme: Theme;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Se ejecuta antes de hidratar para aplicar la clase `dark` sin parpadeo.
export const THEME_INIT_SCRIPT = `
(function () {
  try {
    var pref = localStorage.getItem('${STORAGE_KEY}');
    var dark = pref === 'dark' || (pref !== 'light' && window.matchMedia('${QUERY}').matches);
    if (dark) document.documentElement.classList.add('dark');
  } catch (e) {}
})();
`;

function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>("system");
  const [theme, setTheme] = useState<Theme>("light");

  useEffect(() => {
    // localStorage solo existe en el navegador: la preferencia se lee al montar.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreferenceState(readPreference());
  }, []);

  useEffect(() => {
    const media = window.matchMedia(QUERY);
    const apply = () => {
      const next: Theme = preference === "system" ? (media.matches ? "dark" : "light") : preference;
      setTheme(next);
      document.documentElement.classList.toggle("dark", next === "dark");
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Sin almacenamiento (modo privado): la preferencia dura solo esta sesión.
    }
  }, []);

  const value = useMemo(() => ({ theme, preference, setPreference }), [theme, preference, setPreference]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
